import { useCallback, useEffect, useRef } from 'react'
import { useBlocker } from 'react-router-dom'

import { Button } from '../components/Button.js'
import { Sheet } from '../components/Sheet.js'
import { useDeclareUnsaved } from '../pwa/UnsavedWork.js'
import styles from './DiscardGuard.module.css'

export interface DiscardGuard {
  /** Whether this editor is holding anything. Drives the visible indicator. */
  readonly dirty: boolean
  /** Whether a navigation is currently being held up by the guard. */
  readonly blocked: boolean
  /** Let the held-up navigation through, losing what was typed. */
  readonly discard: () => void
  /** Cancel it and stay. */
  readonly keepEditing: () => void
  /**
   * "I am about to leave on purpose."
   *
   * Called immediately before navigating after a successful save. Without it
   * the guard blocks the app's own post-save navigation: `setDirty(false)` and
   * `navigate()` run in the same tick, and the blocker is still armed when the
   * navigation starts because the re-render has not happened yet.
   */
  readonly allowLeaving: () => void
}

/**
 * One fact, three consequences (T-808).
 *
 * "Is the user holding something?" decides the visible indicator, the discard
 * confirmation, **and** whether the service worker's update prompt is deferred.
 * Answering it in three places means the day they disagree is the day someone
 * loses a half-filled editor to a background reload, which is exactly what
 * T-204's rule exists to prevent. So it is one argument, here.
 *
 * Built on the router's own `useBlocker`, which covers the back button and the
 * back gesture as well as taps — the requirement most likely to be skipped,
 * because it is the one that cannot be reached by clicking around the app.
 *
 * **Not for the log flow.** That screen persists its draft as you go and
 * restores it on return, so leaving loses nothing and a confirmation would be
 * asking about something that is not at stake. A guard that fires when there is
 * no risk is how people learn to dismiss the one that matters.
 */
export function useDiscardGuard(dirty: boolean): DiscardGuard {
  useDeclareUnsaved(dirty)
  const leaving = useRef(false)

  // Re-arm as soon as there is something to protect again, so one deliberate
  // exit does not disarm the guard for the rest of the screen's life.
  useEffect(() => {
    if (dirty) {
      leaving.current = false
    }
  }, [dirty])

  const blocker = useBlocker(
    useCallback(
      ({ currentLocation, nextLocation }) => {
        /*
         * Only a navigation that actually leaves this screen.
         *
         * A `Sheet` closes by popping its own history entry (T-803), which is
         * a navigation to the *same* path. Blocking that put the router and the
         * browser's history out of step and blanked the app — the guard held a
         * POP the sheet had already committed to. It is also plainly the right
         * rule: this guard exists to stop you leaving an editor, and staying on
         * it is not leaving.
         */
        if (currentLocation.pathname === nextLocation.pathname) {
          return false
        }
        return dirty && !leaving.current
      },
      [dirty],
    ),
  )

  return {
    dirty,
    blocked: blocker.state === 'blocked',
    discard: () => blocker.proceed?.(),
    keepEditing: () => blocker.reset?.(),
    allowLeaving: () => {
      leaving.current = true
    },
  }
}

interface Props {
  readonly guard: DiscardGuard
  /**
   * What is at stake, in the editor's own words — "this track", "your changes
   * to Morning". The confirmation names it rather than asking "are you sure",
   * which tells the user nothing they did not already know.
   */
  readonly what: string
}

/** The indicator and the confirmation, both driven by the same guard. */
export function UnsavedWorkGuard({ guard, what }: Props) {
  return (
    <>
      {guard.dirty && (
        <p className={styles['indicator']} role="status">
          <span className={styles['dot']} aria-hidden="true" />
          Unsaved changes
        </p>
      )}

      <Sheet
        open={guard.blocked}
        title="Discard your changes?"
        // A question with two answers below it. A corner control would be a
        // third way to leave, and the ambiguous one.
        dismissLabel={null}
        onClose={guard.keepEditing}
      >
        <div className={styles['confirm']}>
          <p>
            {what} {'has unsaved changes. Leaving now loses them — nothing that is '}
            already saved is affected.
          </p>
          <div className={styles['confirmActions']}>
            <Button onClick={guard.keepEditing}>Keep editing</Button>
            <Button variant="danger" onClick={guard.discard}>
              Discard changes
            </Button>
          </div>
        </div>
      </Sheet>
    </>
  )
}
